import { Fragment, type ReactNode, useLayoutEffect } from "react";

import {
  type GateDecisionOutcome,
  type GateInstanceDecideOutcome,
  type GateInstanceRow,
  APPROVAL_READING_WORDS,
  decideGateInstance,
  describedApprovalRequest,
  deskOwnToolCall,
  subscriptionApprovalBlocked,
  notifyGateInstancesChanged,
  SUBSCRIPTION_CANCEL_GATE_KEY,
} from "~/neucleos/gateInstancesClient";
import { approvalExpiresAt, useApprovalRequestContext } from "~/neucleos/approvalRequestContext";
import { invitationLinkOwner, showInvitationLinkOnce } from "~/neucleos/invitationLinkOnce";
import { useTouchIdApproval } from "~/neucleos/subscriptionTouchIdClient";
import { AgentQuestionCard, agentQuestionOf } from "./AgentQuestionCard";
import { CleoMark } from "./CleoMark";
import { APPROVAL_DETAILS_UNSEEN } from "./chat/ComposerPendingApprovalActions";
import {
  PRESSED_CARD_CLASS,
  PRESSED_LABEL,
  approvalPressKey,
  useApprovalPress,
} from "./approvalPress";
import { randomUUID } from "~/lib/utils";
import type { InboxBoardCommand } from "./InboxBoard";
import { describeApproval } from "./chat/approvalPresentation";
import { RequestText } from "./RequestText";
import {
  approvalCallIncomplete,
  approvalCallRequestText,
  REQUEST_NOT_SHOWN,
} from "@cleo/shared/approvalCall";
import { isWebHost } from "~/webHost";
import { InboxCloudCard, useInboxCloudCard } from "./InboxCloudCard";

// The Inbox's approval card, moved unchanged out of InboxBoard.tsx (7 October
// 2026) so the board and the agent strip's Inbox popover (#1307) can render
// the one card. A second copy is how two cards come to disagree about what
// may be approved.

/**
 * The card's one decision path: the decide lane with the instance's EXACT
 * targetDigest and a fresh decision key, then the gate refresh bus on the
 * lane's own success. The board and the agent strip's popover both decide
 * through this, so neither can drift from the other. The server decides; a
 * later decline overrules an approval there (#1301).
 */
export async function decideGateCard(
  worldKey: string,
  command: Extract<InboxBoardCommand, { readonly type: "decideGateInstance" }>,
): Promise<GateInstanceDecideOutcome> {
  // #1730: the sign-in that pressed Approve; a link that comes back after it no longer owns this window is dropped.
  const owner = invitationLinkOwner();
  const outcome = await decideGateInstance({
    worldKey,
    gateInstanceKey: command.gateInstanceKey,
    targetDigest: command.targetDigest,
    outcome: command.outcome,
    decisionKey: randomUUID(),
    ...(command.answer !== undefined ? { answer: command.answer } : {}),
  });
  if (outcome.ok) notifyGateInstancesChanged();
  // #1730: an invitation link this Approve made is shown once, here on this device, above whichever board decided it.
  if (outcome.ok && outcome.invitationLink) showInvitationLinkOnce(outcome.invitationLink, owner);
  return outcome;
}

/** The estate accent order: user accent first, World accent beneath. */
export const ACCENT = "var(--ncl-accent, var(--ncl-world-accent))";
export const ACCENT_FOREGROUND = "var(--ncl-accent-foreground, var(--ncl-world-accent-foreground))";

/** A Pocket conversation's approval whose turn ended before it was used (W-7e4c91a2): the World projects it cancelled. */
export const gateCancelled = (row: GateInstanceRow): boolean => row.effectStatus === "cancelled";
export const GATE_CANCELLED_APPROVED = "Approved, but cancelled and unusable";
export const GATE_CANCELLED = "Cancelled · Cleo stopped before this was decided";
/** A cancelled request whose card shows the World's own sentence for why (below the state). */
export const GATE_CANCELLED_SAID = "Cancelled";

/** What a request the person stopped says, on the World's card and this Desk's alike. */
export const GATE_STOPPED = "You stopped this request, so it can no longer be approved.";
/** What a request says when this Desk's stop record cannot show it was not stopped (round 2 P2-g). */
export const GATE_STOP_UNCONFIRMED =
  "Cleo's Desk can't confirm this request wasn't stopped; ask Cleo again.";
/** The state of a request this Desk stopped that the World still shows waiting (it could not be told). */
export const GATE_STOPPED_HERE_LABEL = "Stopped · only Decline is left";

/**
 * The World's own sentence for a cancelled request (the person's Stop withdrew it, or its turn ended), when it gave
 * one; a request the World cancelled as stopped without one says so in the same words.
 */
export const gateCancelledSentence = (row: GateInstanceRow): string | null =>
  !gateCancelled(row)
    ? null
    : row.cancelledSentence !== undefined && row.cancelledSentence !== ""
      ? row.cancelledSentence
      : row.invalidatedReason === "stopped"
        ? GATE_STOPPED
        : null;

/**
 * W-16dc173f: a request this Desk recorded as stopped that the World still shows waiting (an older World without the
 * withdraw door, or one that did not answer). Its card says it was stopped and offers only Decline; this Desk's decide
 * route never approves it.
 */
export const gateStoppedHere = (row: GateInstanceRow): boolean =>
  (row.stoppedHere === true || row.stopUnconfirmed === true) && !gateCancelled(row);

export function gateReceiptLabel(row: GateInstanceRow): string {
  if (gateCancelledSentence(row) !== null) return GATE_CANCELLED_SAID;
  if (gateStoppedHere(row) && row.state === "CREATED")
    return row.stoppedHere === true
      ? GATE_STOPPED_HERE_LABEL
      : "Not confirmed · only Decline is left";
  if (gateCancelled(row))
    return row.state === "DECIDED" && row.outcome !== "declined"
      ? GATE_CANCELLED_APPROVED
      : GATE_CANCELLED;
  if (row.state === "CREATED" && row.humanDecides) return "Awaiting your decision";
  if (row.outcome === "declined") return "Declined";
  if (row.decider === "cleo") return "Cleo handled the decision";
  if (row.decider === "self-request") return "Authorized by your request";
  if (row.state === "SATISFIED") return "Receipt recorded · no approval required";
  return row.state === "DECIDED" ? "Decision recorded" : "Receipt recorded";
}

/** W-4022cbfa: what a card says when this Mac has no record of the call it asks about. */
export const GATE_REQUEST_MISSING_WORDS =
  "What this changes is not on this Mac: Cleo asked from another device or an earlier session. The conversation where Cleo asked shows it; open it before you approve.";
/** W-4022cbfa: what a card says first for a call it cannot describe in plain words. */
export const GATE_REQUEST_UNDESCRIBED_WORDS =
  "Cleo asks to take a step this Desk cannot yet put in plain words. Read the exact request below before you approve.";

/**
 * W-7d543e69: what Cleo asks to do, before Approve or Decline — the exact call
 * from her own approval record (the chat card shows the same call). A gate
 * opened on another device has no record on this Mac and says so. A call
 * described in plain words (W-f9a102e0: a Canvas share — what, with whom,
 * which rights, until when) says that first; its exact fields stay one click
 * away under "The exact request".
 */
function ApprovalRequestDetail({
  row,
  awaiting,
  named,
}: {
  readonly row: GateInstanceRow;
  readonly awaiting: boolean;
  /** The pane's one read of the names (see HitlPane): Approve and these details wait on the same state. */
  readonly named: ReturnType<typeof useApprovalRequestContext>;
}) {
  // W-f9a102e0 / W-6082d348: the Canvas, the people and the World the call
  // names, by name; the plain words wait for them (reading) instead of
  // saying "a Canvas" first.
  if (!row.request) {
    return awaiting ? (
      <p className="text-xs text-muted-foreground" data-cleo="inbox-hitl-request-missing">
        {GATE_REQUEST_MISSING_WORDS}
      </p>
    ) : null;
  }
  // W-dc12e842: the same structured identity the conversation's card reads (the call the Desk recorded beside the
  // request); the request's text is shown, never read for which tool runs.
  const described = describedApprovalRequest(row.request, {
    ...named.context,
    cardFacts: row.cardFacts,
  });
  const call = row.request.call ?? null;
  // Not the Desk's own tool call: it reads as what Approve will run, by its kind, as on the conversation's card.
  const willRun =
    described.plain === null && call !== null && deskOwnToolCall(call) === null
      ? describeApproval(
          {
            requestKind:
              call.kind === "file-change"
                ? "file-change"
                : call.kind === "file-read"
                  ? "file-read"
                  : "command",
            detail: row.request.detail,
            call,
          },
          named,
        )
      : null;
  const exact = (fields: ReactNode) => (
    <details className="mt-2" data-cleo="inbox-hitl-request-exact">
      <summary className="cursor-pointer text-[10.5px] text-muted-foreground">
        The exact request
      </summary>
      {fields}
    </details>
  );
  // W-dc12e842 (Codex pass 2): the complete request, never clipped: the structured call with every argument whole
  // (each file's changes, a long value in full), or an older record's text. The view scrolls.
  const complete = call !== null ? approvalCallRequestText(call) : row.request.detail;
  const fields = (
    <pre
      className="mt-2 max-h-[min(50vh,28rem)] overflow-auto whitespace-pre-wrap break-words font-mono text-[10.5px] text-foreground"
      data-cleo="inbox-hitl-request-complete"
    >
      <RequestText text={complete} />
    </pre>
  );
  return (
    <div
      aria-busy={described.plain !== null && named.reading}
      className="rounded-lg border border-border/40 p-3"
      data-cleo="inbox-hitl-request"
    >
      <p className="text-xs font-medium text-foreground">What Cleo asks to do</p>
      {row.request.conversationTitle !== null ? (
        <p className="mt-0.5 truncate text-[10.5px] text-muted-foreground">
          From the conversation “{row.request.conversationTitle}”
        </p>
      ) : null}
      {willRun !== null ? (
        <>
          <p className="mt-2 text-sm text-foreground" data-cleo="inbox-hitl-request-will-run">
            <RequestText text={willRun.headline} />
          </p>
          {willRun.supporting.length > 0 ? (
            <p className="mt-1 whitespace-pre-line text-xs text-muted-foreground">
              <RequestText text={willRun.supporting.join("\n")} />
            </p>
          ) : null}
          {willRun.code ? (
            <pre
              className="mt-2 whitespace-pre-wrap break-words font-mono text-[10.5px] text-foreground"
              data-cleo="inbox-hitl-request-code"
            >
              <RequestText text={willRun.code} />
            </pre>
          ) : null}
          {willRun.approvalBlocked != null ? (
            <p className="mt-1.5 text-xs text-warning" role="status">
              {willRun.approvalBlocked}
            </p>
          ) : null}
          {complete !== willRun.code ? (
            <pre
              className="mt-2 max-h-[min(50vh,28rem)] overflow-auto whitespace-pre-wrap break-words font-mono text-[10.5px] text-foreground"
              data-cleo="inbox-hitl-request-raw"
            >
              <RequestText text={complete} />
            </pre>
          ) : null}
        </>
      ) : described.plain !== null ? (
        <>
          {named.reading ? (
            <p
              className="mt-2 text-sm text-muted-foreground"
              data-cleo="inbox-hitl-request-reading"
            >
              {APPROVAL_READING_WORDS}
            </p>
          ) : (
            <>
              <p className="mt-2 text-sm text-foreground" data-cleo="inbox-hitl-request-plain">
                <RequestText text={described.plain.headline} />
              </p>
              <dl className="mt-1.5 grid grid-cols-[72px_minmax(0,1fr)] gap-x-2 gap-y-1 text-xs">
                {described.plain.rows.map(([label, text], index) => (
                  // A stored card may repeat a label, and its rows never move: they keep their place by position.
                  // oxlint-disable-next-line react/no-array-index-key
                  <Fragment key={index}>
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd className="whitespace-pre-wrap break-words text-foreground">
                      <RequestText text={text} />
                    </dd>
                  </Fragment>
                ))}
              </dl>
            </>
          )}
          {described.plain.exact?.length && !named.reading ? (
            // W-d729ba9b: the exact characters behind the plain words, one tap away.
            <details className="mt-2" data-cleo="inbox-hitl-exact-changes">
              <summary className="cursor-pointer text-[10.5px] text-muted-foreground">
                Show exact changes
              </summary>
              <pre className="mt-2 max-h-[min(50vh,28rem)] overflow-auto whitespace-pre-wrap break-words font-mono text-[10.5px] text-foreground">
                <RequestText text={described.plain.exact.join("\n\n")} />
              </pre>
            </details>
          ) : null}
          {exact(fields)}
        </>
      ) : call !== null && described.fields !== null ? (
        // W-4022cbfa / W-e0e51d23: a call this Desk cannot yet say in plain words
        // is never shown as its tool and fields first; they stay one click away.
        <>
          <p className="mt-2 text-sm text-foreground" data-cleo="inbox-hitl-request-undescribed">
            {GATE_REQUEST_UNDESCRIBED_WORDS}
          </p>
          {exact(fields)}
        </>
      ) : (
        fields
      )}
    </div>
  );
}

/**
 * The gate pane: what Cleo is asking, and the person's decision — approve or
 * decline through the decide lane with the instance's EXACT targetDigest.
 * A decided or refused instance renders its honest state; nothing here
 * pretends a decision landed before the lane says so.
 */
export function HitlPane({
  row,
  decidePending,
  decideError,
  onCommand,
  worldKey,
  onApproveAvailableChange,
}: {
  readonly row: GateInstanceRow | null;
  readonly decidePending: boolean;
  readonly decideError: string | null;
  readonly onCommand: (command: InboxBoardCommand) => void;
  readonly worldKey: string | null;
  /**
   * The agent strip's one-key answers ask the card itself whether its Approve
   * may be pressed by anything but the person's own click on it: never a Touch
   * ID card, never while the card keeps Approve unavailable. `until` is when
   * its Approve stops on its own (a prepared change's expiry). The board passes
   * nothing and is unchanged.
   */
  readonly onApproveAvailableChange?: (available: boolean, until?: number | null) => void;
}) {
  // A call whose facts the card must show (stopping a subscription renewing)
  // keeps Approve unavailable until they have loaded from the World, and for
  // good when this card carries no request to show them from.
  const request = row?.request ?? null;
  // One read for the whole card (review, 19 Oct): Approve's wait and the details' wait are the same state, so the
  // details can never be reading while Approve is on, however the pane remounts them. It is the card's own identity
  // that keys the 5 s fallback. This is a wait in the UI, not enforcement by the World.
  const named = useApprovalRequestContext(worldKey, request, row?.gateInstanceKey ?? null);
  // Step 7.4.4a-2b: in a window with no Mac (cleo-web), a card-only approval of a World conversation is shown and decided on
  // that conversation's own card (InboxCloudCard), through the door Pocket uses; on the Desk this asks nothing.
  const cloudCard = useInboxCloudCard(
    worldKey,
    isWebHost() && row?.cardApproved === true ? row : null,
  );
  const { context: requestContext, reading, recheck } = named;
  const requestPlain =
    request === null
      ? null
      : describedApprovalRequest(request, { ...requestContext, cardFacts: row?.cardFacts }).plain;
  const described = request !== null && requestPlain !== null;
  const blocked =
    subscriptionApprovalBlocked(
      row?.gateKey ?? null,
      request,
      requestPlain,
      row?.memberChangeKey ?? null,
      row?.cardApproved === true,
    ) ??
    // W-dc12e842 (fail closed): an engine's approval request (a command, a file change, more access, a consent) whose
    // card cannot show it in full (an older record with no structured call, a call missing what it runs) offers only
    // Decline. A World gate instance's own card (a World door's gate, a call the World held, a gate with no record on
    // this Mac) is described from the World's record and keeps its Approve.
    (row?.request?.engineRequest === true && approvalCallIncomplete(request?.call)
      ? REQUEST_NOT_SHOWN
      : null);
  // Its Approve is the person's Touch ID over the exact words (never a click alone).
  const touchIdCard = row?.gateKey === SUBSCRIPTION_CANCEL_GATE_KEY;
  const touchId = useTouchIdApproval(touchIdCard, worldKey, row?.gateInstanceKey ?? null);
  const approveBlocked = blocked ?? touchId.unavailable;
  // A card Cleo was stopped on offers only Decline (W-16dc173f), so the
  // strip's keys never offer its Approve either (#1371).
  // W-f7556677: nor while the card's plain words are still being read (until they show, or APPROVAL_CONTEXT_WAIT_MS),
  // as the conversation's composer waits (ComposerPendingApprovalActions `unseen`): nothing is approved unseen.
  // Decision 7: a coding agent's question is answered on its own card (a choice and a note), never by a one-key Approve.
  const agentQuestion = agentQuestionOf(row?.cardFacts);
  // The conversation's own card (7.4.4a-2b) takes its Approve from a click on it alone: the strip's keys never answer it.
  const approveAvailable =
    row !== null &&
    agentQuestion === null &&
    cloudCard === null &&
    !touchIdCard &&
    approveBlocked === null &&
    !reading &&
    !gateStoppedHere(row);
  const approveUntil = approveAvailable ? approvalExpiresAt(requestContext) : null;
  // Reported in the commit that draws it (a layout effect, #1365 round 1): a
  // card that turns its Approve off, closes or remounts says so before any
  // other task can run, so nothing can send in between. A card that closes
  // reports nothing available until it says again.
  useLayoutEffect(() => {
    onApproveAvailableChange?.(approveAvailable, approveUntil);
    return () => onApproveAvailableChange?.(false, null);
  }, [approveAvailable, approveUntil, onApproveAvailableChange]);
  // W-8af4eaef: the pressed button says so in the click's own commit and the
  // card dims; the lane's refusal (decideError) brings the card back as it was.
  const { pressed, press, release } = useApprovalPress(
    row === null ? null : approvalPressKey("gate", row.gateInstanceKey),
    decideError,
  );
  if (cloudCard !== null) return <InboxCloudCard card={cloudCard} />;
  if (row === null) {
    return (
      <div
        className="flex flex-1 items-center justify-center text-xs text-muted-foreground"
        data-cleo="inbox-hitl-gone"
      >
        This gate is no longer awaiting you.
      </div>
    );
  }
  const awaiting = row.state === "CREATED" && row.humanDecides && !gateCancelled(row);
  // W-16dc173f: a request the person stopped is never approved here, whatever its card could show.
  const stoppedHere = gateStoppedHere(row);
  const decide = (outcome: GateDecisionOutcome) => {
    if (!press(outcome === "approved" ? "approve" : "decline")) return;
    const send = () =>
      onCommand({
        type: "decideGateInstance",
        gateInstanceKey: row.gateInstanceKey,
        targetDigest: row.targetDigest,
        outcome,
      });
    // A platform Components release is read once more before Approve acts; moved, nothing is sent and the card says why.
    const rechecked = outcome === "approved" ? recheck() : null;
    if (rechecked === null) return send();
    void rechecked.then((why) => (why === null ? send() : release()));
  };
  const deciding = decidePending || pressed !== null;
  // W-77f2f204: what is asked scrolls; the decision stays below it, always in
  // view. A Canvas create lists its whole page and once ran Approve and
  // Decline off the bottom of a pane that did not scroll.
  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-3"
      data-cleo="inbox-hitl-pane"
      data-gate-instance={row.gateInstanceKey}
    >
      <div
        aria-busy={pressed !== null || undefined}
        className={`flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto${pressed !== null ? ` ${PRESSED_CARD_CLASS}` : ""}`}
        data-approval-pressed={pressed?.decision}
        data-cleo="inbox-hitl-asked"
      >
        <div className="flex items-start gap-3 rounded-lg border border-border/40 p-3">
          <CleoMark
            color="var(--ncl-section-answers)"
            className="mt-0.5 size-5 shrink-0"
            decorative
          />
          <div className="min-w-0 flex-1">
            {/* W-4022cbfa: a call described in plain words is titled by what will happen
                (below, "What Cleo asks to do"), not by its gate's general title; a gate's
                id and kind are never shown to a person. */}
            {described ? null : (
              <p className="text-sm text-foreground" data-cleo="inbox-hitl-title">
                {row.title}
              </p>
            )}
            <dl
              className={`${described ? "" : "mt-2 "}grid grid-cols-[72px_minmax(0,1fr)] gap-x-2 gap-y-1 text-[10.5px]`}
            >
              <dt className="text-muted-foreground">Decider</dt>
              <dd className="text-foreground">
                {row.decider === "cleo"
                  ? "Cleo, on your behalf"
                  : row.decider === "self-request"
                    ? "Your request"
                    : row.humanDecides
                      ? "You"
                      : "No approval required"}
              </dd>
              <dt className="text-muted-foreground">State</dt>
              <dd className="text-foreground" data-cleo="inbox-hitl-state">
                {gateReceiptLabel(row)}
              </dd>
            </dl>
          </div>
        </div>
        {gateCancelledSentence(row) !== null ? (
          <p className="text-xs text-foreground" data-cleo="inbox-hitl-cancelled" role="status">
            {gateCancelledSentence(row)}
          </p>
        ) : null}
        {stoppedHere ? (
          <p className="text-xs text-foreground" data-cleo="inbox-hitl-stopped" role="status">
            {row.stoppedHere === true ? GATE_STOPPED : GATE_STOP_UNCONFIRMED}
          </p>
        ) : null}
        {agentQuestion !== null ? (
          <AgentQuestionCard
            // A draft note belongs to its own question: another question's card starts empty (Codex review of #2955).
            key={row.gateInstanceKey}
            question={agentQuestion}
            awaiting={awaiting}
            pending={deciding}
            error={null}
            onAnswer={({ outcome, answer }) => {
              if (!press(outcome === "approved" ? "approve" : "decline")) return;
              onCommand({
                type: "decideGateInstance",
                gateInstanceKey: row.gateInstanceKey,
                targetDigest: row.targetDigest,
                outcome,
                ...(answer !== undefined ? { answer } : {}),
              });
            }}
          />
        ) : (
          <ApprovalRequestDetail row={row} awaiting={awaiting && !stoppedHere} named={named} />
        )}
        {!awaiting && (
          <p className="text-xs text-muted-foreground">
            This receipt records the gate's decision. Completion of the action must be checked in
            its result.
          </p>
        )}
      </div>
      {awaiting && agentQuestion === null ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2" data-cleo="inbox-hitl-decide">
          {/* W-93f772c5: the row wraps, and a warning takes a whole line of its
              own, so in a narrow place (the agent strip's popover) it never
              lies over Approve and Decline. */}
          {stoppedHere ? null : (
            <button
              className="rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-60"
              data-cleo="inbox-hitl-approve"
              disabled={
                deciding ||
                approveBlocked !== null ||
                reading ||
                touchId.pending ||
                touchId.checking
              }
              title={reading ? APPROVAL_DETAILS_UNSEEN : undefined}
              onClick={() =>
                touchIdCard
                  ? void touchId.approve().then((landed) => {
                      if (landed) notifyGateInstancesChanged();
                    })
                  : decide("approved")
              }
              style={{ background: ACCENT, color: ACCENT_FOREGROUND }}
              type="button"
            >
              {pressed?.decision === "approve"
                ? PRESSED_LABEL.approve
                : touchIdCard
                  ? "Approve with Touch ID"
                  : "Approve"}
            </button>
          )}
          <button
            className="rounded-md border border-border/60 px-3 py-1.5 text-xs text-foreground hover:bg-accent/40 disabled:opacity-60"
            data-cleo="inbox-hitl-decline"
            disabled={deciding}
            onClick={() => decide("declined")}
            type="button"
          >
            {pressed?.decision === "decline" ? PRESSED_LABEL.decline : "Decline"}
          </button>
          {decidePending ? (
            <span className="text-[10px] text-muted-foreground">Recording your decision…</span>
          ) : null}
          {touchId.pending ? (
            <span className="text-[10px] text-muted-foreground">Waiting for Touch ID…</span>
          ) : null}
          {stoppedHere ? null : approveBlocked !== null ? (
            <span
              className="basis-full text-[10px] text-warning"
              data-cleo="inbox-hitl-blocked"
              role="status"
            >
              {approveBlocked}
            </span>
          ) : touchId.message !== null ? (
            <span
              className="basis-full text-[10px] text-warning"
              data-cleo="inbox-hitl-touch-id"
              role="status"
            >
              {touchId.message}
            </span>
          ) : null}
        </div>
      ) : null}
      {decideError !== null ? (
        <p
          className="shrink-0 text-[11px] text-warning"
          data-cleo="inbox-hitl-decide-error"
          role="alert"
        >
          {decideError}
        </p>
      ) : null}
    </div>
  );
}
